import React, { useState, useEffect, useRef } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Animated,
  SafeAreaView,
} from "react-native";
import Icon from "react-native-vector-icons/FontAwesome";
import SwipeCard from "../components/SwipeCard";
import Toggler from "../components/Toggler";
import { useLikedNames } from "../context/LikedNamesContext";
import { useAppContext } from "../context/AppContext";
import eeuuMale from "../../data/eeuu-male.json";
import eeuuFemale from "../../data/eeuu-female.json";
import italyMale from "../../data/italy-male.json";
import italyFemale from "../../data/italy-female.json";
import spainMale from "../../data/spain-male.json";
import spainFemale from "../../data/spain-female.json";
import swedenMale from "../../data/sweden-male.json";
import swedenFemale from "../../data/sweden-female.json";
import ukMale from "../../data/uk-male.json";
import ukFemale from "../../data/uk-female.json";

const HomeScreen = ({ navigation }: { navigation: any }) => {
  const pan = useRef(new Animated.ValueXY()).current;
  const opacity = useRef(new Animated.Value(1)).current;

  const [countryIndices, setCountryIndices] = useState({
    spain: { male: 0, female: 0 },
    eeuu: { male: 0, female: 0 },
    italy: { male: 0, female: 0 },
    sweden: { male: 0, female: 0 },
    uk: { male: 0, female: 0 },
  });

  const [names, setNames] = useState({ male: [], female: [] });
  const { addLikedName } = useLikedNames();
  const { nameOriginCountry } = useAppContext();
  const [selectedSex, setSelectedSex] = useState<"male" | "female" | "both">(
    "female"
  );
  const [isMaleTurn, setIsMaleTurn] = useState(false);

  const selectedSexRef = useRef(selectedSex);
  const nameOriginCountryRef = useRef(nameOriginCountry);
  const isMaleTurnRef = useRef(isMaleTurn);
  const currentNameRef = useRef<string | null>(null);

  useEffect(() => {
    selectedSexRef.current = selectedSex;
  }, [selectedSex]);

  useEffect(() => {
    nameOriginCountryRef.current = nameOriginCountry;
  }, [nameOriginCountry]);

  useEffect(() => {
    isMaleTurnRef.current = isMaleTurn;
  }, [isMaleTurn]);

  useEffect(() => {
    if (selectedSex === "both") {
      currentNameRef.current = getNextBothName();
    } else {
      const currentCountry = nameOriginCountry.toLowerCase();
      const currentIndex = countryIndices[currentCountry]?.[selectedSex] || 0;
      currentNameRef.current = names[selectedSex][currentIndex] || null;
    }
  }, [selectedSex, nameOriginCountry, countryIndices, names, isMaleTurn]);

  const getNextBothName = () => {
    const currentCountry = nameOriginCountry.toLowerCase();
    const maleIndex = countryIndices[currentCountry]?.male || 0;
    const femaleIndex = countryIndices[currentCountry]?.female || 0;

    if (isMaleTurn) {
      if (maleIndex < names.male.length) {
        return names.male[maleIndex];
      }
    } else {
      if (femaleIndex < names.female.length) {
        return names.female[femaleIndex];
      }
    }
    return null;
  };

  useEffect(() => {
    const loadNamesData = () => {
      let maleData = [];
      let femaleData = [];
      const normalizedCountry = (nameOriginCountry || "spain").toLowerCase();

      switch (normalizedCountry) {
        case "eeuu":
          maleData = eeuuMale;
          femaleData = eeuuFemale;
          break;
        case "italy":
          maleData = italyMale;
          femaleData = italyFemale;
          break;
        case "spain":
          maleData = spainMale;
          femaleData = spainFemale;
          break;
        case "sweden":
          maleData = swedenMale;
          femaleData = swedenFemale;
          break;
        case "uk":
          maleData = ukMale;
          femaleData = ukFemale;
          break;
        default:
          console.error("Unsupported country: ", nameOriginCountry);
      }

      if (!countryIndices[normalizedCountry]) {
        setCountryIndices((prev) => ({
          ...prev,
          [normalizedCountry]: { male: 0, female: 0 },
        }));
      }

      setNames({ male: maleData, female: femaleData });
    };

    loadNamesData();
  }, [nameOriginCountry]);

  useEffect(() => {
    if (selectedSex === "male") {
      setIsMaleTurn(true);
    } else if (selectedSex === "female") {
      setIsMaleTurn(false);
    }
  }, [selectedSex]);

  const handleSwipe = (direction: "left" | "right") => {
    const currentCountry = nameOriginCountryRef.current.toLowerCase();
    const currentName = currentNameRef.current;

    if (currentName) {
      if (direction === "right") {
        const currentSex =
          selectedSexRef.current === "both"
            ? isMaleTurnRef.current
              ? "male"
              : "female"
            : selectedSexRef.current;
        addLikedName(currentName, currentSex, currentCountry);
      }

      setCountryIndices((prev) => {
        const updatedIndices = {
          ...prev,
          [currentCountry]: {
            ...prev[currentCountry],
            [selectedSexRef.current === "both"
              ? isMaleTurnRef.current
                ? "male"
                : "female"
              : selectedSexRef.current]:
              (prev[currentCountry]?.[
                selectedSexRef.current === "both"
                  ? isMaleTurnRef.current
                    ? "male"
                    : "female"
                  : selectedSexRef.current
              ] || 0) + 1,
          },
        };
        return updatedIndices;
      });

      if (selectedSexRef.current === "both") {
        const nextIsMaleTurn = !isMaleTurnRef.current;
        setIsMaleTurn(nextIsMaleTurn);
        isMaleTurnRef.current = nextIsMaleTurn;
      }
    }

    Animated.timing(pan, {
      toValue: { x: direction === "right" ? 500 : -500, y: 0 },
      duration: 200,
      useNativeDriver: false,
    }).start(() => {
      pan.setValue({ x: 0, y: 0 });

      opacity.setValue(0);
      Animated.timing(opacity, {
        toValue: 1,
        duration: 330,
        useNativeDriver: false,
      }).start();
    });
  };

  const handleDislike = () => {
    handleSwipe("left");
  };

  const handleLike = () => {
    handleSwipe("right");
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View style={styles.centeredContainer}>
        {/* Toggler */}
        <View style={styles.togglerContainer}>
          <Toggler selectedSex={selectedSex} setSelectedSex={setSelectedSex} />
        </View>

        {/* SwipeCard */}
        <View style={styles.cardContainer}>
          {selectedSex === "both"
            ? getNextBothName() && (
                <SwipeCard
                  pan={pan}
                  opacity={opacity}
                  cardData={getNextBothName()}
                  onSwipe={handleSwipe}
                  isMaleTurn={isMaleTurn}
                />
              )
            : names[selectedSex].length > 0 && (
                <SwipeCard
                  pan={pan}
                  opacity={opacity}
                  cardData={
                    names[selectedSex][
                      countryIndices[nameOriginCountry.toLowerCase()]?.[
                        selectedSex
                      ] || 0
                    ]
                  }
                  onSwipe={handleSwipe}
                  isMaleTurn={isMaleTurn}
                />
              )}
        </View>

        {/* Like/Dislike Buttons */}
        <View style={styles.buttonsContainer}>
          <TouchableOpacity
            style={[styles.button, styles.dislikeButton]}
            onPress={handleDislike}
            activeOpacity={0.7}
          >
            <Icon name="times" size={30} color="#F87171" />
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.button, styles.likeButton]}
            onPress={handleLike}
            activeOpacity={0.7}
          >
            <Icon name="check" size={30} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: "#F9FAFB",
  },
  centeredContainer: {
    flex: 1,
    maxWidth: 375, // Mimic mobile screen width
    alignSelf: "center", // Center horizontally
    alignItems: "center",
    width: "100%", // Allow it to shrink on smaller screens
    backgroundColor: "#F9FAFB",
  },
  togglerContainer: {
    flex: 1, // Allocate 1 part of the space for the toggler
    justifyContent: "center",
    alignItems: "center",
  },
  cardContainer: {
    flex: 4, // Allocate 4 parts of the space for the SwipeCard
    justifyContent: "center",
    alignItems: "center",
    gap: 20,
  },
  buttonsContainer: {
    flex: 1, // Allocate 1 part of the space for the buttons
    flexDirection: "row",
    justifyContent: "space-between",
    alignSelf: "center",
    alignItems: "center",
    width: 280,
  },
  button: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: "center",
    alignItems: "center",
  },
  dislikeButton: {
    backgroundColor: "#fff",
    borderWidth: 2,
    borderColor: "#E16952",
  },
  likeButton: {
    backgroundColor: "#56aa95",
    borderColor: "#fff",
    borderWidth: 2,
  },
  text: {
    fontSize: 18,
    color: "#333",
  },
});

export default HomeScreen;
